import { useCallback, useEffect, useState } from "react";
import { Button, Col, Form, InputGroup, ListGroup, Modal, Row, Spinner, Tab, Tabs } from "react-bootstrap";
import Globals from "../Globals";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faClose, faFilePdf, faMinus, faPlus, faPlusCircle, faSearch } from "@fortawesome/free-solid-svg-icons";
import debounce from "../utils/debounce";
import api from "../services/api";
import searchingImg from '../assets/img/searching.svg';
import noDataImg from '../assets/img/no_data.svg';
import Swal from "sweetalert2";
import apiFormData from "../services/apiFormData";
import { useNavigate } from "react-router-dom";

export default function PreBudgetModal({ show, onHide, selectedBudget, onSave }) {
  const [name, setName] = useState('');

  const [activeTab, setActiveTab] = useState("pdf");

  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');

  const [newMedicineModalOpen, setNewMedicineModalOpen] = useState(false);
  const [medicineName, setMedicineName] = useState("");
  const [presentation, setPresentation] = useState("");
  const [unit, setUnit] = useState("");
  const [posologia, setPosologia] = useState("");
  const [isSpecial, setIsSpecial] = useState(false);

  const [medicines, setMedicines] = useState([]);
  const [selectedMedicines, setSelectedMedicines] = useState([]);

  const [loading, setLoading] = useState(false);

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearch(value);
    }, 500),
    []
  );

  useEffect(() => {
    const searchMedicines = async () => {
      if (search.length !== 0) {
        setLoading(true);

        await api.get(`api/pharmacydrugs/search/${search}`)
          .then((response) => {
            setMedicines(response.data.data)
          })
          .catch((error) => {
            console.error("Erro ao buscar medicamentos:", error);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }

    searchMedicines()
  }, [search])

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value.toUpperCase());
    debouncedSearch(value.toUpperCase());
  };

  const openNewMedicineModal = () => {
    setNewMedicineModalOpen(true);
    setName(inputValue.toUpperCase());
  }

  const closeNewMedicineModal = () => {
    setNewMedicineModalOpen(false);

    setMedicineName('');
    setUnit('');
    setPosologia('');
    setIsSpecial(false);
  }

  const addNewMedicine = async () => {
    setNewMedicineModalOpen(false);

    Swal.fire({
      title: 'Aguarde',
      text: 'Adicionando novo medicamento...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    })

    let form = new FormData();

    form.set('name', name);
    form.set('unit', unit);
    form.set('dosage', posologia);
    form.set('type', isSpecial ? 'Especial' : 'Normal');
    form.set('without_medication', true);

    await apiFormData.post('api/pharmacydrugs', form)
      .then((response) => {
        Swal.close();

        Swal.fire({
          title: 'Sucesso',
          text: 'Medicamento adicionado com sucesso!',
          icon: 'success',
          confirmButtonText: 'OK'
        });

        // refaz a busca
        // marca o medicamento como selecionado
        debouncedSearch(name);
        toggleMedicine(response.data.data.id);
      })
      .catch((error) => {
        console.error("Erro ao adicionar medicamento:", error);
        Swal.fire({
          title: 'Erro',
          text: 'Não foi possível adicionar o medicamento. Tente novamente.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      });
  }

  const isMedicineSelected = (medicine) => {
    return selectedMedicines.some((item) => item.id === medicine.id)
  }

  const toggleMedicine = (medicine) => {
    setSelectedMedicines((prev) => {
      const isCurrentlySelected = prev.some(item => item.id === medicine.id);

      if (isCurrentlySelected) {
        return prev.filter(item => item.id !== medicine.id);
      } else {
        return [...prev, { ...medicine, quantity: 1 }];
      }
    });
  };

  const setQuantity = (medicine, delta) => {
    setSelectedMedicines((prev) => {
      return prev.map((item) => {
        if (item.id === medicine.id) {
          const newQuantity = Math.max(1, item.quantity + delta) // Mínimo de 1
          return { ...item, quantity: newQuantity }
        }
        return item
      })
    })
  }

  const getQuantity = (medicine) => {
    const item = selectedMedicines.find((item) => item.id === medicine.id)
    return item ? item.quantity : 1
  }

  const formatMoney = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  }

  const total = selectedMedicines.reduce((acc, selectedItem) => {
    return acc + (selectedItem.unit_price || 0) * selectedItem.quantity
  }, 0)

  const savePreBudget = async (e) => {
    e.preventDefault();

    Swal.fire({
      title: 'Aguarde...',
      text: 'Confirmando pré-orçamento...',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
      }
    });

    const payload = {
      budget_id: selectedBudget.attendance_id,
      shopper_id: 14, // ta vindo nulo da impressora
      name,
      items: selectedMedicines.map(medicine => ({
        pharmacy_drug_id: medicine.id,
        prescription: medicine.name,
        quantity: medicine.quantity,
        price: medicine.unit_price
      }))
    }

    await api.post('api/cashiers/pos/budget/pre', payload)
    .then(response => {
      console.log(response)
      const pdfPath = Globals.BASE_URL + '/storage/' + response.data.path

      selectedBudget.image_path_pre = pdfPath;

      Swal.close();

      onHide();

      Swal.fire({
        icon: 'success',
        title: 'Pré-orçamento confirmado com sucesso!',
        html: 'Um arquivo PDF foi gerado.',
        showConfirmButton: true,
        confirmButtonColor: '#56CAF0',
        showDenyButton: true,
        denyButtonText: 'Abrir PDF',
        denyButtonColor: '#00468B',
        allowOutsideClick: false,
        preDeny: () => {
          window.open(pdfPath, '_blank');
          //return false; // impede o swal de fechar
        }
      })
        .then((result) => {
          if (result.isConfirmed) {
            if (onSave) onSave();
          }
        });
    })
    .catch(error => {
      console.error(error)
    })
  }

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Pré-Orçamento</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="gy-4" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          <Col lg={6}>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-3"
            >
              <Tab eventKey="pdf" title="Arquivo da solicitação">
                <div className="border rounded bg-light p-3 h-100 d-flex justify-content-center align-items-center">
                  {selectedBudget?.image_path ? (
                    <div className="w-100 h-100" style={{ minHeight: '500px' }}>
                      <iframe
                        src={`${Globals.BASE_URL}/storage/${selectedBudget.image_path}#navpanes=0`}
                        width="100%"
                        height="100%"
                        style={{ border: 'none', minHeight: '500px' }}
                        title="Visualização do arquivo da prescrição"
                      />
                    </div>
                  ) : (
                    <div className="text-center text-muted">
                      <FontAwesomeIcon icon={faFilePdf} size="4x" className="mb-3" />
                      <div className="h5 fw-semibold">PDF não disponível</div>
                      <div className="text-muted small">
                        Nenhum arquivo PDF foi encontrado para este atendimento.
                      </div>
                    </div>
                  )}
                </div>
              </Tab>

              <Tab eventKey="medicines" title={`Medicamentos Selecionados (${selectedMedicines.length})`}>
                {selectedMedicines.length === 0 ? (
                  <div className="text-center text-muted p-5">
                    Nenhum medicamento selecionado.
                  </div>
                ) : (
                  <div className="bg-light p-2 rounded" style={{ maxHeight: "500px", overflowY: "auto" }}>
                    <ListGroup>
                      {selectedMedicines.map((medicine) => (
                        <ListGroup.Item key={medicine.id} className="d-flex justify-content-between align-items-center mb-2">
                          <div className="d-flex flex-column">
                            <span className="fw-semibold">{medicine.name}</span>
                            <span>{medicine.presentation}</span>
                          </div>

                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() =>
                              setSelectedMedicines((prev) => prev.filter((sm) => sm.id !== medicine.id))
                            }
                          >
                            <FontAwesomeIcon icon={faClose} size="lg" />
                          </Button>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                )}
              </Tab>
            </Tabs>
          </Col>

          <Col lg={6}>
            <h5 className="fw-semibold mb-3">Informações do pré-orçamento</h5>

            <Form onSubmit={savePreBudget}>
              <Form.Group className="mb-3">
                <Form.Label>Nome completo</Form.Label>
                <Form.Control
                  value={name}
                  onChange={(e) => setName(e.target.value.toUpperCase())}
                  placeholder="Nome completo do paciente"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Buscar Medicamentos</Form.Label>

                <InputGroup className="mb-3">
                  <InputGroup.Text id="basic-addon1" className="mr-1">
                    <FontAwesomeIcon icon={faSearch} />
                  </InputGroup.Text>

                  <Form.Control
                    type="text"
                    placeholder="Busque por nome"
                    value={inputValue}
                    onChange={handleInputChange}
                  />

                  <Button
                    //variant="primary"
                    className="ms-2 rounded-1 primary-background text-white primary-border"
                    onClick={openNewMedicineModal}
                  >
                    <FontAwesomeIcon
                      icon={faPlusCircle}
                      size="sm"
                      className="me-2"
                    />
                    Novo Medicamento
                  </Button>
                </InputGroup>

                <div className="border rounded" style={{ height: "325px", overflowY: "auto" }}>
                  <ListGroup variant="flush">
                    {search.length === 0 ? (
                      selectedMedicines.length > 0 ?
                        (
                          selectedMedicines.map((med) => {
                            const isSelected = isMedicineSelected(med);

                            return (
                              <ListGroup.Item
                                key={med.id}
                                className={`d-flex align-items-center p-2 rounded transition ${isSelected ? "border primary-border blue-200" : ""
                                  }`}
                              >
                                <Form.Check
                                  type="checkbox"
                                  id={`med-${med.id}`}
                                  checked={isSelected}
                                  onChange={() => toggleMedicine(med)}
                                  className="me-2"
                                />
                                <div className="d-flex justify-content-between w-100">
                                  <label
                                    htmlFor={`med-${med.id}`}
                                    className="d-flex justify-content-between align-items-center cursor-pointer w-100 mb-0"
                                    style={{ fontSize: "0.875rem" }}
                                  >
                                    <div className={`col-5 ${isSelected ? "primary-color" : ""}`}>
                                      <div>
                                        {med.name}
                                        <br />
                                        {med.presentation}
                                      </div>
                                    </div>

                                    {med.without_medication && (
                                      <span className="text-danger">EM FALTA</span>
                                    )}

                                    {isSelected && (
                                      <div className="d-flex align-items-center gap-1 ms-2">
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          className="rounded-circle p-0 d-flex align-items-center justify-content-center"
                                          style={{
                                            width: "1.5rem",
                                            height: "1.5rem",
                                            minWidth: "1.5rem",
                                          }}
                                          onClick={() => setQuantity(med, -1)}
                                        >
                                          <FontAwesomeIcon
                                            icon={faMinus}
                                            style={{ fontSize: "0.75rem" }}
                                          />
                                        </Button>
                                        <span
                                          className="fw-medium text-center"
                                          style={{
                                            minWidth: "1.5rem",
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {getQuantity(med)}
                                        </span>
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          className="rounded-circle p-0 d-flex align-items-center justify-content-center"
                                          style={{
                                            width: "1.5rem",
                                            height: "1.5rem",
                                            minWidth: "1.5rem",
                                          }}
                                          onClick={() => setQuantity(med, 1)}
                                        >
                                          <FontAwesomeIcon
                                            icon={faPlus}
                                            style={{ fontSize: "0.75rem" }}
                                          />
                                        </Button>
                                      </div>
                                    )}

                                    {med.unit_price && (
                                      <div className="text-end">
                                        <div className={isSelected ? "primary-color" : ""}>
                                          {formatMoney(med.unit_price)}
                                        </div>
                                      </div>
                                    )}
                                  </label>
                                </div>
                              </ListGroup.Item>
                            );
                          })
                        ) :
                        (
                          <ListGroup.Item className="text-center text-muted py-4 d-flex flex-column align-items-center">
                            <img src={searchingImg} alt="Pesquisa" width={175} className="mb-3" loading="lazy" />
                            Busque os medicamentos no campo acima. <br /> Ou adicione um novo medicamento.
                          </ListGroup.Item>
                        )

                    ) : loading ? (
                      <ListGroup.Item className="text-center py-4 d-flex flex-column align-items-center">
                        <Spinner animation="border" role="status" className="mb-3" style={{ color: '#00468B' }}>
                          <span className="visually-hidden">Carregando...</span>
                        </Spinner>
                        <div className="text-muted">Buscando medicamentos...</div>
                      </ListGroup.Item>
                    ) : medicines.length === 0 ? (
                      <ListGroup.Item className="text-center text-danger py-4 d-flex flex-column align-items-center">
                        <img src={noDataImg} alt="Sem resultados" width={175} className="mb-3" loading="lazy" />
                        Nenhum medicamento encontrado.
                        <Button className="flex items-center mt-2" onClick={openNewMedicineModal}>
                          <FontAwesomeIcon
                            icon={faPlusCircle}
                            size="sm"
                            className="me-2"
                          />
                          Novo Medicamento
                        </Button>
                      </ListGroup.Item>
                    ) : (
                      medicines.map((med) => {
                        const isSelected = isMedicineSelected(med);

                        return (
                          <ListGroup.Item
                            key={med.id}
                            className={`d-flex align-items-center p-2 rounded transition ${isSelected ? "border primary-border blue-200" : ""
                              }`}
                          >
                            <Form.Check
                              type="checkbox"
                              id={`med-${med.id}`}
                              checked={isSelected}
                              onChange={() => toggleMedicine(med)}
                              className="me-2"
                            />
                            <div className="d-flex justify-content-between w-100">
                              <label
                                htmlFor={`med-${med.id}`}
                                className="d-flex justify-content-between align-items-center cursor-pointer w-100 mb-0"
                                style={{ fontSize: "0.875rem" }}
                              >
                                <div className={`col-5 ${isSelected ? "primary-color" : ""}`}>
                                  <div>
                                    {med.name}
                                    <br />
                                    {med.presentation}
                                  </div>
                                </div>

                                {med.without_medication && (
                                  <span className="text-danger">EM FALTA</span>
                                )}

                                {isSelected && (
                                  <div className="d-flex align-items-center gap-1 ms-2">
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      className="rounded-circle p-0 d-flex align-items-center justify-content-center"
                                      style={{
                                        width: "1.5rem",
                                        height: "1.5rem",
                                        minWidth: "1.5rem",
                                      }}
                                      onClick={() => setQuantity(med, -1)}
                                    >
                                      <FontAwesomeIcon
                                        icon={faMinus}
                                        style={{ fontSize: "0.75rem" }}
                                      />
                                    </Button>
                                    <span
                                      className="fw-medium text-center"
                                      style={{
                                        minWidth: "1.5rem",
                                        fontSize: "0.875rem",
                                      }}
                                    >
                                      {getQuantity(med)}
                                    </span>
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      className="rounded-circle p-0 d-flex align-items-center justify-content-center"
                                      style={{
                                        width: "1.5rem",
                                        height: "1.5rem",
                                        minWidth: "1.5rem",
                                      }}
                                      onClick={() => setQuantity(med, 1)}
                                    >
                                      <FontAwesomeIcon
                                        icon={faPlus}
                                        style={{ fontSize: "0.75rem" }}
                                      />
                                    </Button>
                                  </div>
                                )}

                                {med.unit_price && (
                                  <div className="text-end">
                                    <div className={isSelected ? "primary-color" : ""}>
                                      {formatMoney(med.unit_price)}
                                    </div>
                                  </div>
                                )}
                              </label>
                            </div>
                          </ListGroup.Item>
                        );
                      })
                    )}
                  </ListGroup>
                </div>
              </Form.Group>

              <div className="d-flex flex-column gap-2">
                <div>
                  <p className="fs-4 text-end">Total: {formatMoney(total)}</p>
                </div>

                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" className="flex-grow-1" onClick={onHide}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-grow-1"
                    variant="success"
                    disabled={selectedMedicines.length === 0}
                  >
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Salvar Pré-Orçamento
                  </Button>
                </div>
              </div>
            </Form>

          </Col>
        </Row>

        <Modal show={newMedicineModalOpen} onHide={closeNewMedicineModal}>
          <Modal.Header closeButton>
            <Modal.Title>Novo Medicamento</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Form className="text-center">
              <Form.Group className="mb-3">
                <Form.Label>Nome do Medicamento</Form.Label>
                <Form.Control value={medicineName} type="text" name="name" placeholder="Nome" onChange={value => setMedicineName(value.target.value.toUpperCase())} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Apresentação</Form.Label>
                <Form.Control value={presentation} type="text" name="presentation" placeholder="Apresentação" onChange={value => setPresentation(value.target.value.toUpperCase())} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Unidade</Form.Label>
                <select className="form-select" onChange={value => setUnit(value.target.value)} defaultValue={'DEFAULT'}>
                  <option value="DEFAULT" disabled hidden>Selecione a Unidade</option>
                  <option value="AdT">ADESIVO TRANSDÉRMICO</option>
                  <option value="Amp">AMPOLA</option>
                  <option value="Bg">BISNAGA</option>
                  <option value="Bl">BLISTER</option>
                  <option value="Cp">COMPRIMIDO</option>
                  <option value="Cj">CONJUNTO</option>
                  <option value="Ct">CARTELA</option>
                  <option value="Cx">CAIXA</option>
                  <option value="Cap">CÁPSULA</option>
                  <option value="Crt">CERATO (g)</option>
                  <option value="Crm">CREME (g)</option>
                  <option value="Drg">DRÁGEA</option>
                  <option value="Elx">ELIXIR (ml)</option>
                  <option value="Env">ENVELOPE</option>
                  <option value="Fl">FLACONETE</option>
                  <option value="Frs">FRASCO</option>
                  <option value="g">GRAMA (g)</option>
                  <option value="Gel">GEL (g)</option>
                  <option value="Kt">KIT</option>
                  <option value="Lt">LATA</option>
                  <option value="Lo">LOÇÃO (g)</option>
                  <option value="mg">MILIGRAMA (mg)</option>
                  <option value="ml">MILILITRO (ml)</option>
                  <option value="Pst">PASTILHA</option>
                  <option value="Pct">PACOTE</option>
                  <option value="Pmd">POMADA (g)</option>
                  <option value="Pt">POTE</option>
                  <option value="Sch">SACHÊ</option>
                  <option value="Sup">SUPOSITÓRIO</option>
                  <option value="Tb">TUBO</option>
                  <option value="Und">UNIDADE</option>
                  <option value="Ugn">UNGÜENTO (g)</option>
                  <option value="Ovu">ÓVULO</option>
                  <option value="Vd">VIDRO</option>
                  <option value="Xp">XAROPE (ml)</option>
                </select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Posologia</Form.Label>
                <textarea style={{ height: '15vh' }} className="form-control" value={posologia} type="text" name="posologia" placeholder="Posologia" onChange={value => setPosologia(value.target.value.toUpperCase())}></textarea>
              </Form.Group>
              <Form.Group className="mb-3 text-center">
                <Form.Label>Tipo de Medicamento</Form.Label>
                <div>
                  <div className="form-check form-check-inline">
                    <input onChange={() => {
                      setIsSpecial(!isSpecial)
                    }} className="form-check-input" type="radio" name="inlineRadioOptions" id="inlineRadio2" checked={!isSpecial} />
                    <label className="form-check-label" htmlFor="inlineRadio2">Normal</label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input onChange={() => {
                      setIsSpecial(!isSpecial)
                    }} className="form-check-input" type="radio" name="inlineRadioOptions" id="inlineRadio1" checked={isSpecial} />
                    <label className="form-check-label" htmlFor="inlineRadio1">Especial</label>
                  </div>
                </div>
              </Form.Group>
            </Form>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={closeNewMedicineModal}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={addNewMedicine} disabled={!name || !unit || !posologia}>
              Salvar Medicamento
            </Button>
          </Modal.Footer>
        </Modal>
      </Modal.Body>
    </Modal>
  )
}